import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Card, Spin, Alert as AntAlert } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTheme } from '../../contexts/useTheme';
import type { EventJob, JobType, JobStatus } from '../../api/eventJobsService';
import { JOB_TYPE_CONFIG, JOB_STATUS_CONFIG } from '../../api/eventJobsService';
import type { Session } from '../../api/eventsService';
import './JobsTimeline.css';

dayjs.extend(utc);
dayjs.extend(timezone);

interface JobsTimelineProps {
  jobs: EventJob[];
  sessions?: Session[];
  eventStartDate: string;  // YYYY-MM-DD
  eventEndDate: string;    // YYYY-MM-DD
  circuitTimeZone?: string;
  loading?: boolean;
  onJobClick?: (job: EventJob) => void;
}

// Job type definitions for swim lanes
const JOB_TYPE_LANES: Array<{ type: JobType; label: string; color: string; icon: string }> = [
  { type: 'backup', label: 'Backup', color: JOB_TYPE_CONFIG.backup.color, icon: JOB_TYPE_CONFIG.backup.icon },
  { type: 'daily_forecast', label: 'Daily', color: JOB_TYPE_CONFIG.daily_forecast.color, icon: JOB_TYPE_CONFIG.daily_forecast.icon },
  { type: 'hourly_forecast', label: 'Hourly', color: JOB_TYPE_CONFIG.hourly_forecast.color, icon: JOB_TYPE_CONFIG.hourly_forecast.icon },
  { type: 'minutely_forecast', label: 'Minutely', color: JOB_TYPE_CONFIG.minutely_forecast.color, icon: JOB_TYPE_CONFIG.minutely_forecast.icon },
  { type: 'weather_message', label: 'Weather', color: JOB_TYPE_CONFIG.weather_message.color, icon: JOB_TYPE_CONFIG.weather_message.icon },
  { type: 'stations_status_check', label: 'Stations', color: JOB_TYPE_CONFIG.stations_status_check.color, icon: JOB_TYPE_CONFIG.stations_status_check.icon },
  { type: 'radar_status_check', label: 'Radar', color: JOB_TYPE_CONFIG.radar_status_check.color, icon: JOB_TYPE_CONFIG.radar_status_check.icon },
  { type: 'archive_observations', label: 'Archive', color: JOB_TYPE_CONFIG.archive_observations.color, icon: JOB_TYPE_CONFIG.archive_observations.icon },
  { type: 'test', label: 'Test', color: JOB_TYPE_CONFIG.test.color, icon: JOB_TYPE_CONFIG.test.icon },
];

// Get status color
const getStatusColor = (status: JobStatus): string => {
  return JOB_STATUS_CONFIG[status]?.color || '#8c8c8c';
};

// Get status color with alpha
function getStatusColorWithAlpha(status: JobStatus, alpha: number = 1): string {
  const color = getStatusColor(status);
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Group jobs by their type for swim lane display
function groupJobsByType(jobs: EventJob[]): Map<JobType, EventJob[]> {
  const groups = new Map<JobType, EventJob[]>();
  
  // Initialize all type groups
  JOB_TYPE_LANES.forEach(lane => {
    groups.set(lane.type, []);
  });
  
  for (const job of jobs) {
    const group = groups.get(job.job_type);
    if (group) {
      group.push(job);
    }
  }
  
  return groups;
}

export const JobsTimeline: React.FC<JobsTimelineProps> = ({
  jobs,
  sessions = [],
  eventStartDate,
  eventEndDate,
  circuitTimeZone,
  loading = false,
  onJobClick,
}) => {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [hoveredJob, setHoveredJob] = useState<EventJob | null>(null);
  const [hoveredSession, setHoveredSession] = useState<Session | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [canvasRenderKey, setCanvasRenderKey] = useState(0);
  
  // Group jobs by type for swim lanes
  const jobsByType = useMemo(() => groupJobsByType(jobs), [jobs]);
  
  // Update current time every minute
  useEffect(() => {
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    
    const alignTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 60 * 1000);
      
      return () => clearInterval(interval);
    }, msUntilNextMinute);
    
    return () => clearTimeout(alignTimeout);
  }, []);
  
  // Re-draw canvas when container size changes (e.g. window resize)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      setCanvasRenderKey((prev) => prev + 1);
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [loading, eventStartDate, eventEndDate]);
  
  // Get event time bounds
  const getEventBounds = useCallback(() => {
    const tz = circuitTimeZone || 'UTC';
    const startDate = dayjs.tz(eventStartDate, tz).startOf('day').toDate();
    const endDate = dayjs.tz(eventEndDate, tz).endOf('day').toDate();
    return { start: startDate, end: endDate };
  }, [eventStartDate, eventEndDate, circuitTimeZone]);
  
  // Convert date to pixel position
  const dateToX = useCallback((date: Date, bounds: { start: Date; end: Date }, width: number): number => {
    const totalDuration = bounds.end.getTime() - bounds.start.getTime();
    const elapsed = date.getTime() - bounds.start.getTime();
    return (elapsed / totalDuration) * width;
  }, []);
  
  // Layout constants
  const LABEL_WIDTH = 65; // Width for type labels on the left
  const LANE_HEIGHT = 18; // Height of each swim lane
  const LANE_PADDING = 2; // Padding between lanes
  const SESSION_LABEL_HEIGHT = 14; // Height for session labels at top
  const DATE_LABEL_HEIGHT = 22; // Height for date labels at bottom
  const MARKER_SIZE = 8; // Size of job markers (diamonds)
  
  // Calculate canvas height based on swim lanes
  const canvasHeight = SESSION_LABEL_HEIGHT + (LANE_HEIGHT + LANE_PADDING) * JOB_TYPE_LANES.length + DATE_LABEL_HEIGHT;
  
  // Draw timeline
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const bounds = getEventBounds();
    const { width, height } = canvas.getBoundingClientRect();
    
    // Set canvas resolution for retina displays
    canvas.width = width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    const isDark = theme === 'dark';
    const timelineWidth = width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    // Helper to convert date to X position within the timeline area
    const dateToTimelineX = (date: Date) => {
      return timelineLeft + dateToX(date, bounds, timelineWidth);
    };
    
    // Clear canvas with background color
    ctx.fillStyle = isDark ? '#141414' : '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Draw past time overlay
    const nowX = dateToTimelineX(currentTime);
    if (currentTime > bounds.start) {
      const pastEndX = Math.min(nowX, width);
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(timelineLeft, 0, pastEndX - timelineLeft, height - DATE_LABEL_HEIGHT);
    }
    
    const totalDays = (bounds.end.getTime() - bounds.start.getTime()) / (1000 * 60 * 60 * 24);
    const [startYear, startMonth, startDay] = eventStartDate.split('-').map(Number);
    
    // Draw quarter-day lines
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    
    for (let day = 0; day <= Math.ceil(totalDays); day++) {
      for (const hour of [6, 12, 18]) {
        const date = new Date(bounds.start.getTime() + (day * 24 + hour) * 60 * 60 * 1000);
        const x = dateToTimelineX(date);
        
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height - DATE_LABEL_HEIGHT);
        ctx.stroke();
      }
    }
    
    // Draw day boundary lines
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
    ctx.lineWidth = 1;
    ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'center';
    
    for (let day = 0; day <= Math.ceil(totalDays); day++) {
      const date = new Date(bounds.start.getTime() + day * 24 * 60 * 60 * 1000);
      const x = dateToTimelineX(date);
      
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      
      const labelDate = new Date(startYear, startMonth - 1, startDay + day);
      const dayOfWeek = labelDate.toLocaleDateString('en-US', { weekday: 'short' });
      const dateLabel = labelDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      ctx.fillText(dayOfWeek, x, height - 12);
      ctx.fillText(dateLabel, x, height - 2);
    }
    
    // Draw sessions as background shading
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    
    sessions.forEach(session => {
      const sessionStart = new Date(session.start_time);
      const sessionEnd = new Date(session.end_time);
      const startX = dateToTimelineX(sessionStart);
      const endX = dateToTimelineX(sessionEnd);
      
      // Session background
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(startX, SESSION_LABEL_HEIGHT, endX - startX, height - SESSION_LABEL_HEIGHT - DATE_LABEL_HEIGHT);
      
      // Session label at top
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
      ctx.fillText(session.session_name, (startX + endX) / 2, 10);
    });
    
    // Draw swim lane labels and backgrounds
    ctx.textAlign = 'right';
    ctx.font = '9px sans-serif';
    
    JOB_TYPE_LANES.forEach((lane, index) => {
      const laneY = SESSION_LABEL_HEIGHT + index * (LANE_HEIGHT + LANE_PADDING);
      
      // Lane background (alternating subtle shading)
      if (index % 2 === 0) {
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)';
        ctx.fillRect(timelineLeft, laneY, timelineWidth, LANE_HEIGHT);
      }
      
      // Lane label
      ctx.fillStyle = lane.color;
      ctx.fillText(lane.label, LABEL_WIDTH - 6, laneY + LANE_HEIGHT - 4);
      
      // Lane icon
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(lane.icon, 4, laneY + LANE_HEIGHT - 4);
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'right';
    });
    
    // Draw vertical separator line between labels and timeline
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(timelineLeft, 0);
    ctx.lineTo(timelineLeft, height - DATE_LABEL_HEIGHT);
    ctx.stroke();
    
    // Draw jobs as diamond markers in their respective lanes
    JOB_TYPE_LANES.forEach((lane, laneIndex) => {
      const laneY = SESSION_LABEL_HEIGHT + laneIndex * (LANE_HEIGHT + LANE_PADDING);
      const laneJobs = jobsByType.get(lane.type) || [];
      
      laneJobs.forEach(job => {
        const jobTime = new Date(job.scheduled_at);
        const x = dateToTimelineX(jobTime);
        const centerY = laneY + LANE_HEIGHT / 2;
        
        // Draw diamond marker
        const halfSize = MARKER_SIZE / 2;
        const statusColor = getStatusColor(job.status);
        
        ctx.beginPath();
        ctx.moveTo(x, centerY - halfSize);
        ctx.lineTo(x + halfSize, centerY);
        ctx.lineTo(x, centerY + halfSize);
        ctx.lineTo(x - halfSize, centerY);
        ctx.closePath();
        
        // Fill with status color
        ctx.fillStyle = statusColor;
        ctx.fill();
        
        // Add border for running jobs (animated effect via re-render)
        if (job.status === 'running') {
          ctx.strokeStyle = isDark ? '#fff' : '#000';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });
    });
    
    // Draw NOW line
    if (currentTime >= bounds.start && currentTime <= bounds.end) {
      ctx.strokeStyle = isDark ? 'rgba(255, 77, 79, 0.8)' : 'rgba(255, 77, 79, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(nowX, 0);
      ctx.lineTo(nowX, height - DATE_LABEL_HEIGHT);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // NOW label
      ctx.fillStyle = isDark ? 'rgba(255, 77, 79, 0.9)' : 'rgba(255, 77, 79, 1)';
      ctx.font = 'bold 8px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('NOW', nowX, 8);
    }
    
  }, [jobs, jobsByType, sessions, currentTime, theme, getEventBounds, dateToX, eventStartDate, canvasRenderKey]);
  
  // Check if mouse position is over a session
  const getSessionAtPosition = useCallback((x: number, y: number, bounds: { start: Date; end: Date }, width: number, height: number): Session | null => {
    const timelineWidth = width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    if (x < timelineLeft || y > height - DATE_LABEL_HEIGHT) return null;
    
    for (const session of sessions) {
      const startX = timelineLeft + dateToX(new Date(session.start_time), bounds, timelineWidth);
      const endX = timelineLeft + dateToX(new Date(session.end_time), bounds, timelineWidth);
      
      if (x >= startX && x <= endX) {
        return session;
      }
    }
    
    return null;
  }, [sessions, dateToX]);
  
  // Handle mouse move for hover detection
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const bounds = getEventBounds();
    const timelineWidth = rect.width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    // Check if hovering over any job
    let foundJob: EventJob | null = null;
    
    // Determine which lane the mouse is in
    const laneIndex = Math.floor((y - SESSION_LABEL_HEIGHT) / (LANE_HEIGHT + LANE_PADDING));
    
    if (laneIndex >= 0 && laneIndex < JOB_TYPE_LANES.length && x >= timelineLeft) {
      const lane = JOB_TYPE_LANES[laneIndex];
      const laneJobs = jobsByType.get(lane.type) || [];
      
      // Find job near this x position (within marker size tolerance)
      const tolerance = MARKER_SIZE;
      
      for (const job of laneJobs) {
        const jobTime = new Date(job.scheduled_at);
        const jobX = timelineLeft + dateToX(jobTime, bounds, timelineWidth);
        
        if (Math.abs(x - jobX) <= tolerance) {
          foundJob = job;
          break;
        }
      }
    }
    
    if (foundJob) {
      setHoveredJob(foundJob);
      setHoveredSession(null);
      setTooltipPosition({ x: e.clientX, y: e.clientY - 120 });
      canvas.style.cursor = 'pointer';
    } else {
      setHoveredJob(null);
      canvas.style.cursor = 'default';
      
      const session = getSessionAtPosition(x, y, bounds, rect.width, rect.height);
      
      if (session) {
        setHoveredSession(session);
        setTooltipPosition({ x: e.clientX, y: e.clientY - 80 });
      } else {
        setHoveredSession(null);
        setTooltipPosition(null);
      }
    }
  }, [jobsByType, getEventBounds, dateToX, getSessionAtPosition]);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredJob(null);
    setHoveredSession(null);
    setTooltipPosition(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
  }, []);
  
  const handleClick = useCallback(() => {
    if (hoveredJob && onJobClick) {
      onJobClick(hoveredJob);
    }
  }, [hoveredJob, onJobClick]);
  
  // Format time for tooltip display
  const formatTooltipTime = useCallback((timestamp: string) => {
    const tz = circuitTimeZone || 'UTC';
    return dayjs(timestamp).tz(tz).format('MMM D, HH:mm');
  }, [circuitTimeZone]);
  
  if (loading && jobs.length === 0) {
    return (
      <Card styles={{ body: { padding: '8px 12px' } }}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="small" />
        </div>
      </Card>
    );
  }
  
  if (!eventStartDate || !eventEndDate) {
    return (
      <Card styles={{ body: { padding: '8px 12px' } }}>
        <AntAlert title="No event selected" description="Please select an event to view jobs." type="info" showIcon />
      </Card>
    );
  }
  
  return (
    <div className="jobs-timeline-wrapper">
      {/* Canvas */}
      <div 
        ref={containerRef}
        className="jobs-timeline-container"
        style={{ height: canvasHeight }}
      >
        <canvas
          ref={canvasRef}
          className="jobs-timeline-canvas"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        />
        
        {/* Job Tooltip */}
        {hoveredJob && tooltipPosition && (
          <div
            className={`jobs-timeline-tooltip ${theme === 'dark' ? 'dark' : 'light'}`}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
            }}
          >
            <div className="tooltip-header">
              <span 
                className="tooltip-status"
                style={{ 
                  backgroundColor: getStatusColorWithAlpha(hoveredJob.status, 0.2),
                  color: getStatusColor(hoveredJob.status),
                  borderColor: getStatusColor(hoveredJob.status),
                }}
              >
                {JOB_STATUS_CONFIG[hoveredJob.status].label.toUpperCase()}
              </span>
              <span className="tooltip-type">
                {JOB_TYPE_CONFIG[hoveredJob.job_type].icon} {JOB_TYPE_CONFIG[hoveredJob.job_type].label}
              </span>
            </div>
            <div className="tooltip-time">
              <span>Scheduled: {formatTooltipTime(hoveredJob.scheduled_at)}</span>
            </div>
            {hoveredJob.started_at && (
              <div className="tooltip-time">
                <span>Started: {formatTooltipTime(hoveredJob.started_at)}</span>
              </div>
            )}
            {hoveredJob.completed_at && (
              <div className="tooltip-time">
                <span>Completed: {formatTooltipTime(hoveredJob.completed_at)}</span>
              </div>
            )}
            {hoveredJob.error_message && (
              <div className="tooltip-error">
                {hoveredJob.error_message}
              </div>
            )}
            {hoveredJob.job_config && Object.keys(hoveredJob.job_config).length > 0 && (
              <div className="tooltip-config">
                {Object.entries(hoveredJob.job_config).map(([key, value]) => (
                  <span key={key}>{key}: {String(value)}</span>
                ))}
              </div>
            )}
            <div className="tooltip-hint">Click to edit</div>
          </div>
        )}
        
        {/* Session Tooltip */}
        {hoveredSession && tooltipPosition && !hoveredJob && (() => {
          const tz = circuitTimeZone || 'UTC';
          const formatCalendarDate = (dateStr: string, includeDate: boolean = true) => {
            const dateInEventTZ = dayjs.utc(dateStr).tz(tz);
            if (includeDate) {
              return dateInEventTZ.format('MMM D, HH:mm');
            } else {
              return dateInEventTZ.format('HH:mm');
            }
          };
          
          return (
            <div
              style={{
                position: 'fixed',
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                transform: 'translateX(-50%)',
                backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                color: theme === 'dark' ? '#fff' : '#000',
                padding: '8px 12px',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
                pointerEvents: 'none',
                zIndex: 1000,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                maxWidth: '300px',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{hoveredSession.session_name}</div>
              <div style={{ opacity: 0.8, marginBottom: hoveredSession.description ? '4px' : '0' }}>
                {formatCalendarDate(hoveredSession.start_time, true)}
                {' - '}
                {formatCalendarDate(hoveredSession.end_time, false)}
              </div>
              {hoveredSession.description && (
                <div style={{ 
                  opacity: 0.7, 
                  fontSize: '11px', 
                  whiteSpace: 'normal',
                  lineHeight: '1.4'
                }}>
                  {hoveredSession.description}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

